/**
 * Persist list-page filter state across navigation (sessionStorage).
 * Dayjs / Date values are stored as ISO strings and restored as dayjs when available.
 */

const STORAGE_PREFIX = 'dentrist:listFilters:';

const isPlainObject = (value) =>
  value != null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);

const serializeValue = (value) => {
  if (value == null) {
    return value;
  }
  if (typeof value === 'object' && typeof value.toISOString === 'function' && typeof value.isValid === 'function') {
    // dayjs
    return value.isValid() ? { __dayjs: true, value: value.toISOString() } : null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : { __date: true, value: value.toISOString() };
  }
  if (Array.isArray(value)) {
    return value.map(serializeValue);
  }
  if (isPlainObject(value)) {
    const out = {};
    Object.entries(value).forEach(([k, v]) => {
      out[k] = serializeValue(v);
    });
    return out;
  }
  return value;
};

const reviveValue = (value, dayjs) => {
  if (value == null) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => reviveValue(item, dayjs));
  }
  if (isPlainObject(value)) {
    if (value.__dayjs && typeof value.value === 'string' && dayjs) {
      const parsed = dayjs(value.value);
      return parsed.isValid() ? parsed : null;
    }
    if (value.__date && typeof value.value === 'string') {
      const parsed = new Date(value.value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const out = {};
    Object.entries(value).forEach(([k, v]) => {
      out[k] = reviveValue(v, dayjs);
    });
    return out;
  }
  return value;
};

export function loadPersistedFilters(storageKey, fallback = {}, dayjs = null) {
  if (typeof window === 'undefined' || !storageKey) {
    return fallback;
  }
  try {
    const raw = window.sessionStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (!raw) {
      return fallback;
    }
    const parsed = JSON.parse(raw);
    if (!isPlainObject(parsed)) {
      return fallback;
    }
    return { ...fallback, ...reviveValue(parsed, dayjs) };
  } catch {
    return fallback;
  }
}

export function savePersistedFilters(storageKey, filters) {
  if (typeof window === 'undefined' || !storageKey) {
    return;
  }
  try {
    window.sessionStorage.setItem(
      `${STORAGE_PREFIX}${storageKey}`,
      JSON.stringify(serializeValue(filters ?? {})),
    );
  } catch {
    // ignore quota / private mode errors
  }
}

export function clearPersistedFilters(storageKey) {
  if (typeof window === 'undefined' || !storageKey) {
    return;
  }
  try {
    window.sessionStorage.removeItem(`${STORAGE_PREFIX}${storageKey}`);
  } catch {
    // ignore
  }
}
